import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import DictationInput from '../../components/DictationInput';
import { toast } from 'sonner';
import { Flame, Award } from 'lucide-react';

export default function HerModulesPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [modules, setModules] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const [reflection, setReflection] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [modulePrompts, setModulePrompts] = useState({});
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('peer');
  const [snapshotTarget, setSnapshotTarget] = useState(null);
  const [snapshotText, setSnapshotText] = useState('');
  const [processingTarget, setProcessingTarget] = useState(null);
  const [processingFacilitator, setProcessingFacilitator] = useState('');
  const [processingFacilitatorOriginal, setProcessingFacilitatorOriginal] = useState('');
  const [processingClient, setProcessingClient] = useState('');
  const [processingClientOriginal, setProcessingClientOriginal] = useState('');

  const canEditSnapshots = ['admin', 'coordinator'].includes(user.role);

  const canViewProcessingOriginal = ['client', 'peer_specialist', 'facilitator', 'coordinator', 'admin'].includes(user.role);

  const canToggleView = ['peer_specialist', 'facilitator', 'coordinator', 'admin'].includes(user.role);
  const viewModeValue = user.role === 'referral_source' ? 'referral' : viewMode;

  const completionsByModule = useMemo(() => {
    const map = {};
    completions.forEach(c => { map[c.module_id] = c; });
    return map;
  }, [completions]);

  const groupedModules = useMemo(() => {
    const groups = {};
    modules.forEach(m => {
      const section = m.section || 'Modules';
      if (!groups[section]) groups[section] = [];
      groups[section].push(m);
    });
    return groups;
  }, [modules]);

  const sectionOrder = ['Self', 'Family', 'Community'];
  const sectionKeys = useMemo(() => {
    const keys = Object.keys(groupedModules);
    return [...sectionOrder.filter(k => keys.includes(k)), ...keys.filter(k => !sectionOrder.includes(k))];
  }, [groupedModules]);

  const activeModulePrompts = useMemo(() => {
    if (!activeModule) return [];
    return modulePrompts[activeModule.id] || [];
  }, [activeModule, modulePrompts]);

  useEffect(() => {
    if (!activeModule) return;
    setSelectedPrompt('');
    setReflection('');
    setReflectionRefined('');
  }, [activeModule?.id]);

  useEffect(() => {
    if (!activeModule) return;
    const loadPrompts = async () => {
      setPromptsLoading(true);
      try {
        const res = await api.get(`/ai/module-prompts/${activeModule.id}`, { params: { program: 'her' } });
        if (res.data?.prompts) {
          setModulePrompts(prev => ({ ...prev, [activeModule.id]: res.data.prompts }));
        }
      } catch {
        // fallback handled in UI
      } finally {
        setPromptsLoading(false);
      }
    };
    loadPrompts();
  }, [activeModule]);

  useEffect(() => {
    api.get('/her/modules').then(r => setModules(r.data)).catch(() => {});
    api.get('/her/completions').then(r => setCompletions(r.data)).catch(() => {});
  }, []);

  const handleComplete = async () => {
    if (!activeModule) return;
    if (user.role !== 'client') return toast.error('Only participants can submit completions');
    if (!selectedPrompt) return toast.error('Choose a speaking prompt');
    if (!reflection.trim()) return toast.error('Please add your reflection');
    try {
      await api.post('/her/modules/complete', {
        module_id: activeModule.id,
        reflection_text: reflection.trim(),
        selected_prompt: selectedPrompt
      });
      toast.success('Module submission sent for approval');
      setActiveModule(null);
      setReflection('');
      setSelectedPrompt('');
      api.get('/her/completions').then(r => setCompletions(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to save completion');
    }
  };

  const handleSnapshotUpdate = async () => {
    if (!snapshotTarget || !snapshotText.trim()) return;
    try {
      await api.put(`/her/completions/${snapshotTarget.id}/snapshot`, { snapshot_text: snapshotText.trim() });
      toast.success('Snapshot updated');
      setSnapshotTarget(null);
      setSnapshotText('');
      const r = await api.get('/her/completions');
      setCompletions(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to update snapshot');
    }
  };

  const handleProcessingSave = async () => {
    if (!processingTarget) return;
    const payload = {};
    if (['facilitator', 'coordinator', 'admin'].includes(user.role)) {
      payload.facilitator_original = processingFacilitatorOriginal || processingFacilitator;
    }
    if (['client', 'coordinator', 'admin'].includes(user.role)) {
      payload.client_original = processingClientOriginal || processingClient;
    }
    try {
      await api.post(`/her/completions/${processingTarget.id}/processing`, payload);
      toast.success('Documentation saved');
      setProcessingTarget(null);
      setProcessingFacilitator('');
      setProcessingFacilitatorOriginal('');
      setProcessingClient('');
      setProcessingClientOriginal('');
      const r = await api.get('/her/completions');
      setCompletions(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to save documentation');
    }
  };

  const handleApproval = async (completionId, decision, role) => {
    try {
      const endpoint = role ? `/her/completions/${completionId}/approve/${role}` : `/her/completions/${completionId}/approve`;
      await api.post(endpoint, { decision });
      toast.success(`Module ${decision}`);
      const r = await api.get('/her/completions');
      setCompletions(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to update approval');
    }
  };

  return (
    <div className="space-y-6" data-testid="her-modules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>HER Modules & Milestones</h1>
          <p className="text-muted-foreground text-sm">Complete modules and capture reflections that are refined for documentation.</p>
        </div>
        {canToggleView && (
          <div className="flex gap-2">
            <Button variant="outline" className={`rounded-full ${viewMode === 'peer' ? 'border-rose-400 text-rose-700' : ''}`} onClick={() => setViewMode('peer')} data-testid="her-view-peer">
              View Peer Specialist Version
            </Button>
            <Button variant="outline" className={`rounded-full ${viewMode === 'referral' ? 'border-pink-400 text-pink-700' : ''}`} onClick={() => setViewMode('referral')} data-testid="her-view-referral">
              View Referral Source Version
            </Button>
          </div>
        )}
      </div>

      {sectionKeys.map((section) => (
        <div key={section} className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">{section}</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {groupedModules[section].map(module => {
              const completion = completionsByModule[module.id];
              const canViewOriginal = ['admin', 'coordinator'].includes(user.role);
              const canEditProcessing = ['client', 'facilitator', 'coordinator', 'admin'].includes(user.role);
              const approvals = completion?.approvals || {};
              const approvalStatus = completion?.approval_status === 'pending' ? 'pending_peer' : (completion?.approval_status || 'pending_peer');
              const canApprovePeer = user.role === 'peer_specialist' && approvalStatus === 'pending_peer';
              const canApproveFacilitator = user.role === 'facilitator' && approvalStatus === 'pending_facilitator';
              const canApproveCoordinator = user.role === 'coordinator' && approvalStatus === 'pending_coordinator';
              const canOverride = user.role === 'admin';
              const submittedDate = completion ? new Date(completion.submitted_at || completion.completed_at).toLocaleDateString() : '';
              const statusLabel = completion
                ? approvalStatus === 'approved'
                  ? `Approved ${new Date(completion.approved_at || completion.completed_at).toLocaleDateString()}`
                  : approvalStatus === 'rejected'
                    ? 'Needs Review'
                    : approvalStatus === 'pending_facilitator'
                      ? `Awaiting Facilitator Approval • Submitted ${submittedDate}`
                      : approvalStatus === 'pending_coordinator'
                        ? `Awaiting Coordinator Approval • Submitted ${submittedDate}`
                        : `Awaiting Peer Support Approval • Submitted ${submittedDate}`
                : '';
              const statusClass = approvalStatus === 'approved'
                ? 'bg-emerald-50 text-emerald-700'
                : approvalStatus === 'rejected'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-amber-50 text-amber-700';
              return (
                <Card key={module.id} className="border-0 shadow-sm rounded-2xl" data-testid={`her-module-${module.id}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Flame className="w-4 h-4 text-orange-600" /> {module.name}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">{module.description || 'HER curriculum session'}</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {completion ? (
                      <>
                        <Badge className={statusClass} variant="secondary" data-testid={`her-approval-status-${completion.id}`}>
                          {statusLabel}
                        </Badge>
                        <div className="rounded-xl border border-border bg-muted/30 p-3">
                          <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Reflection</p>
                          <p className="text-sm text-slate-700">
                            {viewModeValue === 'peer'
                              ? completion.reflection_refined_peer || completion.reflection_original
                              : completion.reflection_refined_referral || completion.reflection_original}
                          </p>
                          {completion.selected_prompt && (
                            <p className="text-xs text-muted-foreground mt-2">Prompt: {completion.selected_prompt}</p>
                          )}
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <p className="text-xs font-semibold uppercase text-slate-600 mb-2">Approval Status</p>
                          <div className="grid gap-2 text-xs text-slate-700">
                            <div className="flex items-center justify-between">
                              <span>Peer Support Specialist</span>
                              <Badge variant="secondary" className={approvals.peer_specialist?.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : approvals.peer_specialist?.status === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}>
                                {approvals.peer_specialist?.status || 'pending'}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Facilitator</span>
                              <Badge variant="secondary" className={approvals.facilitator?.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : approvals.facilitator?.status === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}>
                                {approvals.facilitator?.status || 'pending'}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Program Coordinator</span>
                              <Badge variant="secondary" className={approvals.coordinator?.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : approvals.coordinator?.status === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}>
                                {approvals.coordinator?.status || 'pending'}
                              </Badge>
                            </div>
                            {approvals.executive?.status && (
                              <div className="flex items-center justify-between">
                                <span>Executive Override</span>
                                <Badge variant="secondary" className={approvals.executive.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}>
                                  {approvals.executive.status}
                                </Badge>
                              </div>
                            )}
                          </div>
                          {(canApprovePeer || canApproveFacilitator || canApproveCoordinator || canOverride) && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {canApprovePeer && (
                                <>
                                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleApproval(completion.id, 'approved', 'peer')} data-testid={`her-approve-peer-${completion.id}`}>Approve</Button>
                                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleApproval(completion.id, 'rejected', 'peer')} data-testid={`her-reject-peer-${completion.id}`}>Reject</Button>
                                </>
                              )}
                              {canApproveFacilitator && (
                                <>
                                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleApproval(completion.id, 'approved', 'facilitator')} data-testid={`her-approve-facilitator-${completion.id}`}>Facilitator Approve</Button>
                                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleApproval(completion.id, 'rejected', 'facilitator')} data-testid={`her-reject-facilitator-${completion.id}`}>Facilitator Reject</Button>
                                </>
                              )}
                              {canApproveCoordinator && (
                                <>
                                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleApproval(completion.id, 'approved', 'coordinator')} data-testid={`her-approve-coordinator-${completion.id}`}>Coordinator Approve</Button>
                                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleApproval(completion.id, 'rejected', 'coordinator')} data-testid={`her-reject-coordinator-${completion.id}`}>Coordinator Reject</Button>
                                </>
                              )}
                              {canOverride && (
                                <>
                                  <Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700 text-white" onClick={() => handleApproval(completion.id, 'approved', 'executive')} data-testid={`her-override-approve-${completion.id}`}>Executive Approve</Button>
                                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleApproval(completion.id, 'rejected', 'executive')} data-testid={`her-override-reject-${completion.id}`}>Executive Reject</Button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="rounded-xl border border-border bg-white p-3">
                          <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Success Snapshot</p>
                          <p className="text-sm text-slate-700">{completion.snapshot_refined || completion.snapshot_original}</p>
                        </div>
                        {canEditSnapshots && (
                          <Button
                            variant="outline"
                            className="rounded-full"
                            onClick={() => {
                              setSnapshotTarget(completion);
                              setSnapshotText(completion.snapshot_refined || completion.snapshot_original || '');
                            }}
                            data-testid={`her-edit-snapshot-${completion.id}`}
                          >
                            Edit Snapshot
                          </Button>
                        )}
                        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                          <p className="text-xs font-semibold uppercase text-amber-700 mb-1">Certificate</p>
                          <p className="text-sm text-amber-900 flex items-center gap-2"><Award size={14} /> {completion.certificate?.client_name} completed {completion.certificate?.module_name}</p>
                          <p className="text-xs text-amber-700">Issued {completion.certificate?.completed_at?.slice(0, 10)}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                          <p className="text-xs font-semibold uppercase text-slate-600 mb-2">Documentation · Participant + Facilitator</p>
                          {completion.processing_facilitator_refined || completion.processing_client_refined ? (
                            <div className="space-y-2">
                              {completion.processing_facilitator_refined && (
                                <div>
                                  <p className="text-xs font-semibold text-slate-600 mb-1">Facilitator Documentation</p>
                                  <p className="text-sm text-slate-700">{completion.processing_facilitator_refined}</p>
                                  {canViewOriginal && completion.processing_facilitator_original && (
                                    <p className="text-xs text-muted-foreground mt-1">Original: {completion.processing_facilitator_original}</p>
                                  )}
                                </div>
                              )}
                              {completion.processing_client_refined && (
                                <div>
                                  <p className="text-xs font-semibold text-slate-600 mb-1">Participant Reflection</p>
                                  <p className="text-sm text-slate-700">{completion.processing_client_refined}</p>
                                  {canViewOriginal && completion.processing_client_original && (
                                    <p className="text-xs text-muted-foreground mt-1">Original: {completion.processing_client_original}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">No documentation yet.</p>
                          )}
                          {canEditProcessing && (
                            <Button
                              variant="outline"
                              className="rounded-full mt-3"
                              onClick={() => {
                                setProcessingTarget(completion);
                                setProcessingFacilitator(completion.processing_facilitator_refined || completion.processing_facilitator_original || '');
                                setProcessingFacilitatorOriginal(completion.processing_facilitator_original || '');
                                setProcessingClient(completion.processing_client_refined || completion.processing_client_original || '');
                                setProcessingClientOriginal(completion.processing_client_original || '');
                              }}
                              data-testid={`her-processing-${completion.id}`}
                            >
                              {completion.processing_facilitator_refined || completion.processing_client_refined ? 'Update Documentation' : 'Add Documentation'}
                            </Button>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">Reflection required: minimum 5 sentences or 50 words.</p>
                        <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full" onClick={() => setActiveModule(module)} data-testid={`her-complete-${module.id}`}>
                          Complete Module
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      <Dialog open={!!activeModule} onOpenChange={(open) => {
        if (!open) {
          setActiveModule(null);
          setReflection('');
          setSelectedPrompt('');
        }
      }}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle>{activeModule?.name} Reflection</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {user.role !== 'client' && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800" data-testid="her-completion-locked">
                Only participants can submit module completions.
              </div>
            )}
            <div className="space-y-2">
              <p className="text-sm font-semibold">Choose a speaking prompt (required)</p>
              <div className="grid gap-2">
                {promptsLoading && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="her-prompts-loading">
                    Loading speaking prompts...
                  </div>
                )}
                {!promptsLoading && activeModulePrompts.length === 0 && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="her-prompts-empty">
                    Prompts are unavailable right now. Please try again.
                  </div>
                )}
                {!promptsLoading && activeModulePrompts.map((prompt, idx) => (
                  <button
                    key={`item-${idx}`}
                    type="button"
                    className={`rounded-xl border px-3 py-2 text-left text-sm transition-colors ${selectedPrompt === prompt ? 'border-rose-400 bg-rose-50 text-rose-900' : 'border-slate-200 hover:border-rose-300'}`}
                    onClick={() => {
                      setSelectedPrompt(prompt);
                      if (!reflection) setReflection(`${prompt} `);
                    }}
                    data-testid={`her-prompt-${idx}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
            <DictationInput
              value={reflection}
              onChange={(cleaned) => setReflection(cleaned)}
              rows={5}
              placeholder="Speak or type your reflection here..."
              promptContext={null}
              data-testid="her-reflection-input"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">AI refinement will format your response for professional documentation. Minimum 5 sentences or 50 words.</p>
              <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-full" onClick={handleComplete} disabled={user.role !== 'client' || !selectedPrompt} data-testid="her-submit-completion">
                Submit Completion
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!snapshotTarget} onOpenChange={() => setSnapshotTarget(null)}>
        <DialogContent className="rounded-2xl max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit Success Snapshot</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <DictationInput
              value={snapshotText}
              onChange={(cleaned) => setSnapshotText(cleaned)}
              rows={4}
              placeholder="Update the success snapshot in professional language..."
              promptContext={{
                context: 'her_snapshot',
                taskName: snapshotTarget?.module_name || 'HER snapshot',
                taskDescription: 'Success snapshot update',
                category: 'her'
              }}
              data-testid="her-snapshot-input"
            />
            <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full" onClick={handleSnapshotUpdate} data-testid="her-snapshot-save">
              Save Snapshot
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!processingTarget} onOpenChange={() => setProcessingTarget(null)}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle>Documentation: Participant + Facilitator</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {['facilitator', 'coordinator', 'admin'].includes(user.role) && (
              <div className="space-y-2">
                <p className="text-sm font-semibold">Facilitator Documentation</p>
                <DictationInput
                  value={processingFacilitator}
                  onChange={(cleaned, original) => {
                    setProcessingFacilitator(cleaned);
                    setProcessingFacilitatorOriginal(original || cleaned);
                  }}
                  rows={4}
                  placeholder="Share facilitator feedback on growth from start to finish..."
                  promptContext={{
                    context: 'her_processing_facilitator',
                    taskName: processingTarget?.module_name || 'HER processing',
                    taskDescription: `Facilitator feedback on growth for ${processingTarget?.module_name || 'this module'}`,
                    category: 'her'
                  }}
                  data-testid="her-processing-facilitator"
                />
                {canViewProcessingOriginal && processingFacilitatorOriginal && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2" data-testid="her-processing-facilitator-original">
                    <p className="text-[11px] font-semibold text-amber-700 mb-1">Original Dictation</p>
                    <p className="text-xs text-amber-900">{processingFacilitatorOriginal}</p>
                  </div>
                )}
              </div>
            )}
            {user.role === 'client' && (
              <div className="space-y-2">
                <p className="text-sm font-semibold">Participant Reflection</p>
                <DictationInput
                  value={processingClient}
                  onChange={(cleaned, original) => {
                    setProcessingClient(cleaned);
                    setProcessingClientOriginal(original || cleaned);
                  }}
                  rows={4}
                  placeholder="What did you take from this module and the documentation discussion?"
                  promptContext={{
                    context: 'her_processing_client',
                    taskName: processingTarget?.module_name || 'HER processing',
                    taskDescription: `Reflection on ${processingTarget?.module_name || 'this module'} and what you will carry forward`,
                    category: 'her'
                  }}
                  data-testid="her-processing-client"
                />
                {canViewProcessingOriginal && processingClientOriginal && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2" data-testid="her-processing-client-original">
                    <p className="text-[11px] font-semibold text-amber-700 mb-1">Original Dictation</p>
                    <p className="text-xs text-amber-900">{processingClientOriginal}</p>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full" onClick={handleProcessingSave} data-testid="her-processing-save">
                Save Documentation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
